import {
  addDays,
  diffDays,
  fillPreview,
  formatDate,
  rotationSuggestion,
  sameRef,
  scheduleInputSchema,
  sessionPhase,
  topicInputSchema,
  validate,
  waitingMembers,
  type ISODate,
  type PresenterRef,
  type Session,
} from "@love-cpmd/core";
import { Avatar, Badge, Button, cn, EmptyState, Field, Modal, SegmentedControl, Textarea } from "@love-cpmd/ui";
import {
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  CalendarPlus,
  CalendarX2,
  CircleCheck,
  ListOrdered,
  Minus,
  NotebookPen,
  PencilLine,
  Plus,
  RotateCw,
  Sparkles,
  UserRoundCog,
  WandSparkles,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../lib/api";
import { ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useOverlay } from "../../lib/hooks";
import { useAction, useWorld, type World } from "../../lib/queries";
import { CategoryChip, PresenterAvatar } from "../common/session-bits";
import { CategorySelect, OwnerNote, PresenterPicker, SessionDayPicker, TopicInput } from "./pickers";

function focusField(errors: Record<string, string>) {
  const first = Object.keys(errors)[0];
  if (first) window.setTimeout(() => document.getElementById(`f-${first}`)?.focus(), 30);
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="shrink-0 text-body-sm text-on-surface-variant">{label}</span>
      <span className="min-w-0 text-right text-label-lg text-on-surface">{children}</span>
    </div>
  );
}

/* ───────────────────────── F3 Input schedule ───────────────────────── */

export function NewScheduleDialog({ open, onClose, presetDate }: { open: boolean; onClose: () => void; presetDate?: string | null }) {
  const { world } = useWorld();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Input schedule"
      description="Add a presenter to a weekly sharing date."
      icon={<CalendarPlus />}
      size="lg"
      mobile="fullscreen"
      bodyClassName="p-0"
    >
      {world ? <NewScheduleForm world={world} presetDate={presetDate} onDone={onClose} /> : <div className="h-64" />}
    </Modal>
  );
}

function NewScheduleForm({ world, presetDate, onDone }: { world: World; presetDate?: string | null; onDone: () => void }) {
  const { userId } = useAuth();
  // Opened from the general button → suggest the nearest empty date + next member in rotation.
  const suggestion = useMemo(
    () => (presetDate ? null : rotationSuggestion(world.rotation?.status === "locked" ? world.rotationRows : undefined, world.sessions, world.today, world.settings.weekday)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [presenter, setPresenter] = useState<PresenterRef | null>(suggestion?.memberId ? { kind: "member", id: suggestion.memberId } : null);
  const [date, setDate] = useState<ISODate | null>(presetDate ?? suggestion?.date ?? null);
  const [categoryId, setCategoryId] = useState<string>("");
  const [topic, setTopic] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<"form" | "confirm">("form");
  const fromRotation = !!suggestion?.memberId && presenter?.kind === "member" && presenter.id === suggestion.memberId;

  const save = useAction(api.createSchedule, { success: "Schedule saved" });

  const review = () => {
    const result = validate(scheduleInputSchema, { presenter, date: date ?? "", categoryId, topic });
    if (!result.ok) {
      setErrors(result.errors);
      focusField(result.errors);
      return;
    }
    setErrors({});
    setStep("confirm");
  };

  const submit = async () => {
    if (!userId || !presenter || !date) return;
    try {
      await save.run(userId, { presenter, date, categoryId, topic });
      onDone();
    } catch (e) {
      if (e instanceof ApiError) {
        const next = e.code === "DATE_TAKEN" ? { date: e.message } : (e.fields ?? {});
        setErrors(next);
        setStep("form");
        focusField(next);
      }
    }
  };

  if (step === "confirm" && presenter && date) {
    const category = world.categoriesById.get(categoryId);
    return (
      <div className="flex min-h-full flex-col">
        <div className="flex flex-1 flex-col gap-4 px-5 py-5">
          <p className="text-body-md text-on-surface-variant">Please check before saving.</p>
          <div className="rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-4 shadow-card">
            <div className="flex items-center gap-3 border-b border-outline-variant/40 pb-3">
              <PresenterAvatar presenter={presenter} world={world} size="lg" />
              <div>
                <p className="text-label-lg text-on-surface">{world.name(presenter)}</p>
                {fromRotation && (
                  <Badge tone="primary" size="xs" icon={<ListOrdered />}>
                    Suggested from rotation
                  </Badge>
                )}
              </div>
            </div>
            <div className="divide-y divide-outline-variant/30">
              <SummaryRow label="Date">{formatDate(date, "full")}</SummaryRow>
              <SummaryRow label="Time">
                {world.settings.startTime}–{world.settings.endTime} WIB
              </SummaryRow>
              <SummaryRow label="Category">
                <CategoryChip category={category} />
              </SummaryRow>
              <SummaryRow label="Topic">{topic.trim() || <span className="font-normal text-on-surface-variant italic">Not filled yet</span>}</SummaryRow>
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="tonal" onClick={() => setStep("form")} disabled={save.pending}>
            Back
          </Button>
          <Button onClick={submit} loading={save.pending} leading={<CircleCheck />}>
            Yes, save schedule
          </Button>
        </ModalFooter>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-1 flex-col gap-5 px-5 py-5">
        <Field
          label="Presenter"
          required
          htmlFor="f-presenter"
          error={errors.presenter}
          aside={fromRotation ? <Badge tone="primary" size="xs" icon={<ListOrdered />}>Suggested from rotation</Badge> : undefined}
          hint="Members who already have an upcoming schedule can't be picked."
        >
          <PresenterPicker id="f-presenter" world={world} value={presenter} onChange={setPresenter} blockScheduled invalid={!!errors.presenter} />
        </Field>
        <Field label="Date" required error={errors.date}>
          <SessionDayPicker world={world} value={date} onChange={setDate} invalid={!!errors.date} />
        </Field>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label="Topic category" required htmlFor="f-categoryId" error={errors.categoryId}>
            <CategorySelect id="f-categoryId" world={world} value={categoryId} onChange={setCategoryId} invalid={!!errors.categoryId} />
          </Field>
          <Field label="Topic details" optional htmlFor="f-topic" error={errors.topic}>
            <TopicInput id="f-topic" value={topic} onChange={setTopic} invalid={!!errors.topic} placeholder="Can be left empty for now" />
          </Field>
        </div>
      </div>
      <ModalFooter>
        <Button variant="tonal" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={review} trailing={<ArrowRight />}>
          Save schedule
        </Button>
      </ModalFooter>
    </div>
  );
}

/** Sticky footer for forms rendered inside a Modal body with `p-0`. */
function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-outline-variant/40 bg-surface-container-lowest/95 px-5 pt-3.5 pb-[max(env(safe-area-inset-bottom),0.875rem)] backdrop-blur [&>button]:max-md:flex-1">
      {children}
    </div>
  );
}

/* ───────────────────────── Edit topic ───────────────────────── */

export function EditTopicDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const { userId } = useAuth();
  const [categoryId, setCategoryId] = useState(session.categoryId ?? "");
  const [topic, setTopic] = useState(session.topic);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useAction(api.updateTopic, { success: "Topic updated" });
  const submit = async () => {
    const result = validate(topicInputSchema, { categoryId, topic });
    if (!result.ok) return setErrors(result.errors);
    try {
      await save.run(userId!, session.id, result.data);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.fields) setErrors(e.fields);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit topic"
      description={`Session on ${formatDate(session.date, "long")}`}
      icon={<PencilLine />}
      footer={
        <>
          <Button variant="tonal" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.pending}>
            Save topic
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <OwnerNote session={session} world={world} userId={userId} verb="editing" />
        <Field label="Topic category" required htmlFor="f-categoryId" error={errors.categoryId}>
          <CategorySelect id="f-categoryId" world={world} value={categoryId} onChange={setCategoryId} invalid={!!errors.categoryId} />
        </Field>
        <Field label="Topic details" optional htmlFor="f-topic" error={errors.topic} hint="About 100 characters so it fits on cards.">
          <TopicInput id="f-topic" value={topic} onChange={setTopic} invalid={!!errors.topic} placeholder="e.g. Design system governance for multi-brand teams" />
        </Field>
        <p className="rounded-xl bg-surface-container-low px-3 py-2.5 text-body-sm text-on-surface-variant">
          Slides can be uploaded until D-1. The topic can be left empty until D-3 ({formatDate(addDays(session.date, -3), "medium")}).
        </p>
      </div>
    </Modal>
  );
}

/* ───────────────────────── F4 / F5 Change schedule: reschedule or swap ───────────────────────── */

export function ChangeScheduleDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<"reschedule" | "swap">("reschedule");
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change schedule"
      description={`${world.name(session.presenter)} · ${formatDate(session.date, "long")}`}
      icon={<ArrowLeftRight />}
      size="lg"
      mobile="fullscreen"
      bodyClassName="p-0"
    >
      <div className="flex min-h-full flex-col">
        <div className="px-5 pt-5">
          <SegmentedControl
            label="Change type"
            value={mode}
            onChange={setMode}
            className="w-full [&>button]:flex-1"
            options={[
              { value: "reschedule", label: "Reschedule", icon: <RotateCw /> },
              { value: "swap", label: "Swap with a member", icon: <ArrowLeftRight /> },
            ]}
          />
        </div>
        {mode === "reschedule" ? <RescheduleForm session={session} world={world} onDone={onClose} /> : <SwapForm session={session} world={world} onDone={onClose} />}
      </div>
    </Modal>
  );
}

function RescheduleForm({ session, world, onDone }: { session: Session; world: World; onDone: () => void }) {
  const { userId } = useAuth();
  const [date, setDate] = useState<ISODate | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();
  const move = useAction(api.reschedule, { success: "Schedule moved" });
  const submit = async () => {
    if (!date || !userId) return;
    try {
      await move.run(userId, session.id, date, reason);
      onDone();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.code === "DATE_TAKEN" ? e.message : (e.fields?.date ?? e.message));
        setDate(null);
      }
    }
  };
  return (
    <>
      <div className="flex flex-1 flex-col gap-5 px-5 py-5">
        <OwnerNote session={session} world={world} userId={userId} verb="moving" />
        <Field label="Pick a new date" error={error}>
          <SessionDayPicker
            world={world}
            value={date}
            onChange={(d) => {
              setDate(d);
              setError(undefined);
            }}
            currentSession={session}
            invalid={!!error}
          />
        </Field>
        {date && (
          <div className="rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-4 shadow-card">
            <p className="mb-3 text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">Before → after</p>
            <div className="flex items-center gap-3">
              <PresenterAvatar presenter={session.presenter} world={world} size="md" />
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-label-lg">
                <span className="text-on-surface-variant line-through">{formatDate(session.date, "medium")}</span>
                <ArrowRight className="size-4 text-primary" aria-hidden />
                <span className="text-primary">{formatDate(date, "medium")}</span>
              </div>
            </div>
          </div>
        )}
        <Field label="Reason" optional htmlFor="f-reason">
          <Textarea id="f-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Clashes with a client review that week" />
        </Field>
      </div>
      <ModalFooter>
        <Button variant="tonal" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!date} loading={move.pending} leading={<RotateCw />}>
          Move schedule
        </Button>
      </ModalFooter>
    </>
  );
}

function SwapForm({ session, world, onDone }: { session: Session; world: World; onDone: () => void }) {
  const { userId } = useAuth();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const swap = useAction(api.swapSchedules, { success: "Schedules swapped" });
  const candidates = world.sessions.filter(
    (s) => s.id !== session.id && s.status === "scheduled" && s.type !== "spontaneous" && diffDays(s.date, world.today) >= 0,
  );
  const target = candidates.find((c) => c.id === targetId);
  const submit = async () => {
    if (!target || !userId) return;
    try {
      await swap.run(userId, session.id, target.id, { a: session.date, b: target.date }, reason);
      onDone();
    } catch {
      setTargetId(null);
    }
  };
  if (!candidates.length) {
    return (
      <div className="px-5 py-8">
        <EmptyState compact icon={<ArrowLeftRight />} title="No one to swap with" description="Swaps need another member with an upcoming schedule." />
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-1 flex-col gap-5 px-5 py-5">
        <Field label="Choose a member to swap with" hint="Only members with an upcoming schedule are listed.">
          <ul role="radiogroup" className="flex max-h-72 flex-col gap-1.5 overflow-y-auto rounded-2xl bg-surface-container-low p-1.5">
            {candidates.map((c) => {
              const selected = c.id === targetId;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setTargetId(c.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                      selected ? "bg-primary-fixed ring-2 ring-primary-container/50" : "hover:bg-surface-container-lowest",
                    )}
                  >
                    <PresenterAvatar presenter={c.presenter} world={world} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-label-lg text-on-surface">{world.name(c.presenter)}</span>
                      <span className="block truncate text-body-sm text-on-surface-variant">{c.topic || "Topic not set yet"}</span>
                    </span>
                    <span className={cn("shrink-0 text-label-md", selected ? "text-primary" : "text-on-surface-variant")}>{formatDate(c.date, "dayMonth")}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Field>
        {target && (
          <div className="relative grid grid-cols-1 gap-3 md:grid-cols-2">
            <SwapSide label="This session" presenter={session.presenter} world={world} from={session.date} to={target.date} />
            <div className="absolute top-1/2 left-1/2 z-10 hidden size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary-container text-on-primary shadow-brand md:flex">
              <ArrowLeftRight className="size-5" aria-hidden />
            </div>
            <div className="flex justify-center md:hidden">
              <ArrowDown className="size-5 text-primary" aria-hidden />
            </div>
            <SwapSide label="Swap target" presenter={target.presenter} world={world} from={target.date} to={session.date} />
          </div>
        )}
        {target && (
          <p className="rounded-xl bg-secondary-fixed/50 px-3 py-2.5 text-body-sm text-on-secondary-fixed">
            Both schedules change right away. {world.name(target.presenter, "short")}
            {session.presenter.kind === "member" && session.presenter.id !== userId ? ` and ${world.name(session.presenter, "short")}` : ""} will be notified in the portal.
          </p>
        )}
        <Field label="Reason" optional htmlFor="f-swap-reason">
          <Textarea id="f-swap-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Business trip that week" />
        </Field>
      </div>
      <ModalFooter>
        <Button variant="tonal" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!target} loading={swap.pending} leading={<ArrowLeftRight />}>
          Swap schedules
        </Button>
      </ModalFooter>
    </>
  );
}

function SwapSide({ label, presenter, world, from, to }: { label: string; presenter: PresenterRef; world: World; from: ISODate; to: ISODate }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-surface-container-low p-4">
      <span className="text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">{label}</span>
      <div className="flex items-center gap-2.5">
        <PresenterAvatar presenter={presenter} world={world} size="md" />
        <span className="truncate text-label-lg text-on-surface">{world.name(presenter)}</span>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-surface-container-lowest px-3 py-2 text-label-md">
        <span className="text-on-surface-variant line-through">{formatDate(from, "dayMonth")}</span>
        <ArrowRight className="size-3.5 text-primary" aria-hidden />
        <span className="text-primary">{formatDate(to, "medium")}</span>
      </div>
    </div>
  );
}

/* ───────────────────────── F6 Mark done (+ offer summary) ───────────────────────── */

export function MarkDoneDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { open: openParams } = useOverlay();
  const [who, setWho] = useState<"scheduled" | "other">("scheduled");
  const [other, setOther] = useState<PresenterRef | null>(null);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const mark = useAction(api.markDone);

  const submit = async () => {
    if (who === "other" && !other) return setError("Choose who presented");
    try {
      await mark.run(userId!, session.id, who === "other" ? other! : undefined);
      setDone(true);
    } catch {
      /* toast already shown */
    }
  };

  if (done) {
    return (
      <Modal open={open} onClose={onClose} title="Session marked as done" icon={<CircleCheck />} size="sm"
        footer={
          <>
            <Button variant="tonal" onClick={onClose}>
              Later
            </Button>
            <Button leading={<NotebookPen />} onClick={() => navigate(`/summary/${session.id}/edit`)}>
              Add summary now
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-body-md text-on-surface">Add a summary while it's still fresh? If you skip it, a reminder stays on the Dashboard.</p>
          <button
            type="button"
            onClick={() => openParams({ action: "log-extra" })}
            className="flex items-center gap-2 self-start rounded-lg px-1 text-label-md text-primary hover:underline"
          >
            <Sparkles className="size-4" aria-hidden /> Someone else also shared? Log extra sharing
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mark as done"
      description={formatDate(session.date, "long")}
      icon={<CircleCheck />}
      footer={
        <>
          <Button variant="tonal" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={mark.pending}>
            Mark as done
          </Button>
        </>
      }
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-label-md text-on-surface">Who presented?</legend>
        <RadioCard checked={who === "scheduled"} onSelect={() => setWho("scheduled")}>
          <PresenterAvatar presenter={session.presenter} world={world} size="sm" />
          <span className="flex-1">
            <span className="block text-label-lg text-on-surface">{world.name(session.presenter)}</span>
            <span className="block text-body-sm text-on-surface-variant">Scheduled presenter</span>
          </span>
        </RadioCard>
        <RadioCard checked={who === "other"} onSelect={() => setWho("other")}>
          <span className="flex size-8 items-center justify-center rounded-full bg-replacement-soft text-replacement">
            <UserRoundCog className="size-4" aria-hidden />
          </span>
          <span className="flex-1">
            <span className="block text-label-lg text-on-surface">Someone else presented</span>
            <span className="block text-body-sm text-on-surface-variant">Recorded as a Replacement</span>
          </span>
        </RadioCard>
        {who === "other" && (
          <Field error={error} className="mt-2">
            <PresenterPicker
              world={world}
              value={other}
              onChange={(v) => {
                setOther(v);
                setError(undefined);
              }}
              exclude={[session.presenter]}
              suggested={world.rotationRows ? waitingMembers(world.rotationRows).map((r) => r.memberId) : []}
              invalid={!!error}
            />
          </Field>
        )}
      </fieldset>
    </Modal>
  );
}

function RadioCard({ checked, onSelect, children }: { checked: boolean; onSelect: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-left ring-1 transition-all ring-inset",
        checked ? "bg-primary-soft ring-2 ring-primary-container/60" : "bg-surface-container-lowest ring-outline-variant/60 hover:bg-surface-container-low",
      )}
    >
      {children}
      <span className={cn("flex size-5 shrink-0 items-center justify-center rounded-full border-2", checked ? "border-primary-container" : "border-outline")}>
        {checked && <span className="size-2.5 rounded-full bg-primary-container" />}
      </span>
    </button>
  );
}

/* ───────────────────────── Replacement on D-day (PRD 4.9) ───────────────────────── */

export function ReplacementDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const { userId } = useAuth();
  const { open: openParams } = useOverlay();
  const ordered = (world.rotationRows ? waitingMembers(world.rotationRows).map((r) => r.memberId) : []).filter(
    (id) => !(session.presenter.kind === "member" && session.presenter.id === id),
  );
  const [choice, setChoice] = useState<PresenterRef | null>(ordered[0] ? { kind: "member", id: ordered[0] } : null);
  const [manual, setManual] = useState(false);
  const assign = useAction(api.assignReplacement, { success: (s) => `${world.name(s.presenter, "short")} will present instead` });
  const submit = async () => {
    if (!choice) return;
    try {
      await assign.run(userId!, session.id, choice);
      onClose();
    } catch {
      /* toast */
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Replacement presenter"
      description={`${world.name(session.presenter)} can't present on ${formatDate(session.date, "dayMonth")}`}
      icon={<UserRoundCog />}
      footer={
        <>
          <Button variant="danger" className="mr-auto" leading={<CalendarX2 />} onClick={() => openParams({ action: "cancel" })}>
            No replacement — cancel
          </Button>
          <Button onClick={submit} disabled={!choice} loading={assign.pending}>
            Set replacement
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!manual && ordered.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-label-md text-on-surface">Next in rotation</legend>
            {ordered.slice(0, 4).map((id, i) => {
              const ref: PresenterRef = { kind: "member", id };
              return (
                <RadioCard key={id} checked={sameRef(choice ?? undefined, ref)} onSelect={() => setChoice(ref)}>
                  <PresenterAvatar presenter={ref} world={world} size="sm" />
                  <span className="flex-1">
                    <span className="block text-label-lg text-on-surface">{world.memberName(id)}</span>
                    <span className="block text-body-sm text-on-surface-variant">{i === 0 ? "Next in rotation" : `#${i + 1} in the waiting list`}</span>
                  </span>
                </RadioCard>
              );
            })}
          </fieldset>
        )}
        {manual || !ordered.length ? (
          <Field label="Choose a presenter" hint="Any member or guest presenter.">
            <PresenterPicker world={world} value={choice} onChange={setChoice} exclude={[session.presenter]} />
          </Field>
        ) : (
          <button type="button" onClick={() => setManual(true)} className="self-start text-label-md text-primary hover:underline">
            Choose someone else manually
          </button>
        )}
      </div>
    </Modal>
  );
}

/* ───────────────────────── Cancel ───────────────────────── */

export function CancelSessionDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const { userId } = useAuth();
  const [reason, setReason] = useState("");
  const cancel = useAction(api.cancelSession, { success: "Session cancelled" });
  const overdue = sessionPhase(session, world.today) === "overdue";
  const submit = async () => {
    try {
      await cancel.run(userId!, session.id, reason);
      onClose();
    } catch {
      /* toast */
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={overdue ? "Mark as cancelled?" : "Cancel this session?"}
      description={`${world.name(session.presenter)} · ${formatDate(session.date, "long")}`}
      icon={<CalendarX2 />}
      size="sm"
      locked={cancel.pending}
      footer={
        <>
          <Button variant="tonal" onClick={onClose} disabled={cancel.pending}>
            Keep session
          </Button>
          <Button variant="danger-solid" onClick={submit} loading={cancel.pending}>
            {overdue ? "Mark as cancelled" : "Cancel session"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <OwnerNote session={session} world={world} userId={userId} verb="cancelling" />
        <p className="text-body-md text-on-surface-variant">The date becomes free again and the session stays in the history as Cancelled. Cancelled sessions don't need a summary.</p>
        <Field label="Reason" optional htmlFor="f-cancel-reason">
          <Textarea id="f-cancel-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Public holiday" />
        </Field>
      </div>
    </Modal>
  );
}

/* ───────────────────────── Spontaneous sharing (Case F & G) ───────────────────────── */

export function LogExtraDialog({ session, world, open, onClose }: { session: Session; world: World; open: boolean; onClose: () => void }) {
  const { userId } = useAuth();
  const [presenter, setPresenter] = useState<PresenterRef | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [topic, setTopic] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const log = useAction(api.logSpontaneous, { success: "Extra sharing recorded" });
  const submit = async () => {
    try {
      await log.run(userId!, session.id, { presenter, categoryId, topic });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.fields) setErrors(e.fields);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log extra sharing"
      description={`Another presenter on ${formatDate(session.date, "long")} — the schedule doesn't change.`}
      icon={<Sparkles />}
      footer={
        <>
          <Button variant="tonal" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={log.pending}>
            Save extra sharing
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field label="Who shared?" required error={errors.presenter}>
          <PresenterPicker world={world} value={presenter} onChange={setPresenter} invalid={!!errors.presenter} placeholder="Member, guest, or type a name" />
        </Field>
        <Field label="Topic category" required htmlFor="f-extra-cat" error={errors.categoryId}>
          <CategorySelect id="f-extra-cat" world={world} value={categoryId} onChange={setCategoryId} invalid={!!errors.categoryId} />
        </Field>
        <Field label="Topic" required htmlFor="f-extra-topic" error={errors.topic}>
          <TopicInput id="f-extra-topic" value={topic} onChange={setTopic} invalid={!!errors.topic} placeholder="What was shared?" />
        </Field>
      </div>
    </Modal>
  );
}

/* ───────────────────────── F8 Fill schedule from rotation (System Manager) ───────────────────────── */

export function FillRotationDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { world } = useWorld();
  const { userId } = useAuth();
  const waiting = world?.rotationRows ? waitingMembers(world.rotationRows).length : 0;
  const [count, setCount] = useState(Math.min(4, Math.max(1, waiting)));
  const fill = useAction(api.fillFromRotation, { success: (n) => `${n} schedule${n === 1 ? "" : "s"} created` });
  const preview = world?.rotationRows ? fillPreview(world.rotationRows, world.sessions, world.today, world.settings.weekday, count) : [];
  const locked = world?.rotation?.status === "locked";
  const submit = async () => {
    try {
      await fill.run(userId!, preview);
      onClose();
    } catch {
      /* toast + refreshed preview */
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fill schedule from rotation"
      description="Creates schedules for the next empty dates, following the locked order."
      icon={<WandSparkles />}
      size="lg"
      footer={
        <>
          <Button variant="tonal" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!locked || !preview.length} loading={fill.pending}>
            Create {preview.length} schedule{preview.length === 1 ? "" : "s"}
          </Button>
        </>
      }
    >
      {!world ? (
        <div className="h-40" />
      ) : !locked ? (
        <EmptyState compact icon={<ListOrdered />} title="Lock the rotation first" description="Generate and lock the order in the Rotation tab, then fill the schedule from it." />
      ) : !waiting ? (
        <EmptyState compact icon={<CircleCheck />} title="Everyone in this cycle is scheduled" description="Nothing left to fill. Start the next cycle when this one is complete." />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-surface-container-low p-4">
            <div>
              <p className="text-label-lg text-on-surface">How many dates?</p>
              <p className="text-body-sm text-on-surface-variant">{waiting} members are still waiting in cycle {world.rotation?.cycle}.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" aria-label="Fewer" onClick={() => setCount((c) => Math.max(1, c - 1))} disabled={count <= 1}>
                <Minus />
              </Button>
              <span className="w-8 text-center text-headline-md tabular">{count}</span>
              <Button variant="secondary" size="sm" aria-label="More" onClick={() => setCount((c) => Math.min(waiting, c + 1))} disabled={count >= waiting}>
                <Plus />
              </Button>
            </div>
          </div>
          <div>
            <p className="mb-2 text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">Preview</p>
            <ol className="flex flex-col gap-1.5">
              {preview.map((p, i) => (
                <li key={p.date} className="flex items-center gap-3 rounded-xl bg-surface-container-lowest px-3 py-2.5 ring-1 ring-outline-variant/40 ring-inset">
                  <span className="w-5 text-center text-label-md text-on-surface-variant tabular">{i + 1}</span>
                  <span className="w-28 shrink-0 text-label-lg text-on-surface">{formatDate(p.date, "medium")}</span>
                  <Avatar name={world.memberName(p.memberId)} seed={world.membersById.get(p.memberId)?.memberId} size="xs" />
                  <span className="min-w-0 flex-1 truncate text-body-md text-on-surface">{world.memberName(p.memberId)}</span>
                </li>
              ))}
            </ol>
          </div>
          <p className="text-body-sm text-on-surface-variant">Topics stay empty; each presenter fills theirs before D-3. Everyone gets a notification.</p>
        </div>
      )}
    </Modal>
  );
}
